import { useMemo } from 'react';

import type { FeatureToggleDto } from '@/services/ws-feature-toggle/src/main/resources/rest-api';
import { getFeatureToggles, FeatureToggleName } from '@/services/ws-feature-toggle/src/main/resources/rest-api';
import { createContexts } from '@/js/libs/create-contexts';
import { useSubscribe } from '@/js/libs/observable';
import { useIdentityUser } from '@/js/libs/use-auth';
import { NotActive, useRequest } from '@/js/libs/use-request';

type FeaturesMap = {
  [value in FeatureToggleName]?: boolean;
};

const { useFeatureToggleContext } = createContexts({
  FeatureToggle() {
    const user = useIdentityUser();

    const featureTogglesRequest = useRequest({
      apiMethod: getFeatureToggles,
      params: user ? undefined : NotActive,
      queryDefaults: false,
    });

    const featureToggles = useSubscribe(featureTogglesRequest.data$, (response) => response?.data ?? []);
    const pending = useSubscribe(featureTogglesRequest.pending$);
    const { retry } = featureTogglesRequest;

    const featuresMap = useMemo(
      () =>
        featureToggles.reduce<FeaturesMap>((acc, feature) => {
          if (!feature.name || !feature.enabled) {
            return acc;
          }

          return {
            ...acc,
            [feature.name]: feature.enabled,
          };
        }, {}),
      [featureToggles],
    );

    const isManualPostInvoicingEnabled = featuresMap[FeatureToggleName.MANUAL_POST_INVOICING];
    const isScreenRestrictionsEnabled = featuresMap[FeatureToggleName.SCREEN_RESTRICTIONS];
    const isAccountingNumberEnabled = featuresMap[FeatureToggleName.ACCOUNTING_NUMBER];
    const isPositionOverwritingEnabled = featuresMap[FeatureToggleName.POSITION_OVERWRITING];
    const isBriefingEmlEnabled = featuresMap[FeatureToggleName.BRIEFING_EML];
    const isDefaultServiceProductDiscountEnabled = featuresMap[FeatureToggleName.DEFAULT_SERVICE_PRODUCT_DISCOUNT];
    const isDefaultOnlineLineItemsMaxCapacityEnabled = featuresMap[FeatureToggleName.DEFAULT_ONLINE_LINE_ITEMS_MAX_CAPACITY];
    const isReachEnabled = featuresMap[FeatureToggleName.REACH];
    const isOnlineOverbookingLimitEnabled = featuresMap[FeatureToggleName.ONLINE_POSITIONS_OVERBOOKING_VALIDATION];
    const isExistingCustomersReductionEnabled = featuresMap[FeatureToggleName.EXISTING_CUSTOMERS];
    const isPostronMobileInAppDisplaySuppressionEnabled = featuresMap[FeatureToggleName.POSTRON_MOBILE_IN_APP_DISPLAY_SUPPRESSION];
    const isPostronMobileInAppDisplayHidingEnabled = featuresMap[FeatureToggleName.POSTRON_MOBILE_IN_APP_DISPLAY_HIDING];
    const isDynamicCampaignManagementGrossEnabled = featuresMap[FeatureToggleName.DYNAMIC_CAMPAIGN_MANAGEMENT_GROSS];
    const isPathApiEnabled = featuresMap[FeatureToggleName.PATH_API];
    const isDynamicDoohPositionsRenameEnabled = featuresMap[FeatureToggleName.DYNAMIC_DOOH_POSITIONS_RENAME];
    const isDynamicDoohPositionsEnabled = featuresMap[FeatureToggleName.DYNAMIC_DOOH_POSITIONS];
    const isDigitalPositionsCloneEnabled = featuresMap[FeatureToggleName.DYNAMIC_DOOH_POSITIONS_CLONE];
    const isOnlinePositionsRenameEnabled = featuresMap[FeatureToggleName.ONLINE_POSITIONS_RENAME];
    const isMailingPositionsRenameEnabled = featuresMap[FeatureToggleName.MAILING_POSITIONS_RENAME];
    const lastMinuteDiscountCalculationEnabled = !!featuresMap[FeatureToggleName.LAST_MINUTE_DISCOUNT];
    const repeatDiscountCalculationEnabled = !!featuresMap[FeatureToggleName.REPEAT_DISCOUNT];
    const extensionDiscountEnabled = !!featuresMap[FeatureToggleName.EXTENSION_DISCOUNT];
    const deadlineCalculationEnabled = !!featuresMap[FeatureToggleName.DEADLINE_CALCULATION];
    const isInternalAccountToggleEnabled = featuresMap[FeatureToggleName.INTERNAL_ACCOUNT];
    const isPostAccountBsiCodeEnabled = featuresMap[FeatureToggleName.POST_ACCOUNT_BSI_CODE];
    const isFlapPromopostProductEnabled = featuresMap[FeatureToggleName.FLAP_PROMOPOST_PRODUCT];
    const isOnlinePositionsCloneEnabled = featuresMap[FeatureToggleName.ONLINE_POSITIONS_CLONE];
    const isRevisedPromopostDeliveryDaysEnabled = featuresMap[FeatureToggleName.REVISED_PROMOPOST_DELIVERY_DAYS];
    const isPostAppOnlineInventoryEnabled = featuresMap[FeatureToggleName.POST_APP_ONLINE_INVENTORY];
    const isAdditionalPromopostProductsEnabled = featuresMap[FeatureToggleName.ADDITIONAL_PROMOPOST_PRODUCTS];
    const isInMailChannelEnabled = featuresMap[FeatureToggleName.INMAIL_CHANNEL];

    return {
      pending,
      featureToggles: featureToggles as FeatureToggleDto[],
      retry,
      isManualPostInvoicingEnabled,
      isScreenRestrictionsEnabled,
      isAccountingNumberEnabled,
      isPositionOverwritingEnabled,
      isBriefingEmlEnabled,
      isDefaultServiceProductDiscountEnabled,
      isDefaultOnlineLineItemsMaxCapacityEnabled,
      isReachEnabled,
      isOnlineOverbookingLimitEnabled,
      isExistingCustomersReductionEnabled,
      isMobileInAppDisabled: isPostronMobileInAppDisplaySuppressionEnabled,
      isMobileInAppDisabledView: isPostronMobileInAppDisplayHidingEnabled,
      isDynamicCampaignManagementGrossEnabled,
      isPathApiEnabled,
      isDynamicDoohPositionsRenameEnabled,
      isDynamicDoohPositionsEnabled,
      isDigitalPositionsCloneEnabled,
      isOnlinePositionsRenameEnabled,
      isMailingPositionsRenameEnabled,
      lastMinuteDiscountCalculationEnabled,
      repeatDiscountCalculationEnabled,
      extensionDiscountEnabled,
      deadlineCalculationEnabled,
      isInternalAccountToggleEnabled,
      isPostAccountBsiCodeEnabled,
      isFlapPromopostProductEnabled,
      isOnlinePositionsCloneEnabled,
      isRevisedPromopostDeliveryDaysEnabled,
      isPostAppOnlineInventoryEnabled,
      isAdditionalPromopostProductsEnabled,
      isInMailChannelEnabled,
    };
  },
});

export { useFeatureToggleContext };
